import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../common/AuthContext';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { authFetch } from '../common/authFetch';

const TimerContext = createContext();

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const TimerProvider = ({ children }) => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const [activeSession, setActiveSession] = useState(null);
    const [todaySessions, setTodaySessions] = useState([]);
    const [todaySummary, setTodaySummary] = useState([]);
    const [displaySeconds, setDisplaySeconds] = useState(0);

    const secondsRef = useRef(0);
    const timerIntervalRef = useRef(null);
    const checkpointIntervalRef = useRef(null);
    const lastTickRef = useRef(null);
    const sessionRef = useRef(null);

    // Keep sessionRef in sync
    useEffect(() => {
        sessionRef.current = activeSession;
    }, [activeSession]);

    const getTodayRange = () => {
        const now = new Date();
        const start = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
        const end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
        return { start, end };
    };

    const fetchTodaySessions = useCallback(async () => {
        if (!user) return;
        try {
            const { start, end } = getTodayRange();
            const response = await authFetch(
                `${API_URL}/api/v1/study_sessions?startDate=${start.toISOString()}&endDate=${end.toISOString()}`
            );
            const data = await response.json();
            if (data.success) {
                setTodaySessions(data.sessions || []);
            }
        } catch (error) {
            console.error('Error fetching today sessions:', error);
        }
    }, [user]);

    const fetchTodaySummary = useCallback(async () => {
        if (!user) return;
        try {
            const { start, end } = getTodayRange();
            const response = await authFetch(
                `${API_URL}/api/v1/study_sessions/summary?startDate=${start.toISOString()}&endDate=${end.toISOString()}`
            );
            const data = await response.json();
            if (data.success) {
                const summary = data.summary || {};
                setTodaySummary(summary.schedule || summary || []);
            }
        } catch (error) {
            console.error('Error fetching today summary:', error);
        }
    }, [user]);

    // Start the display timer (1-second interval using Date.now() for accuracy)
    const startTimerInterval = useCallback(() => {
        if (timerIntervalRef.current) return;
        lastTickRef.current = Date.now();
        timerIntervalRef.current = setInterval(() => {
            const now = Date.now();
            const elapsed = Math.floor((now - lastTickRef.current) / 1000);
            if (elapsed > 0) {
                secondsRef.current += elapsed;
                lastTickRef.current += elapsed * 1000;
                setDisplaySeconds(secondsRef.current);
            }
        }, 1000);
    }, []);

    const stopTimerInterval = useCallback(() => {
        if (timerIntervalRef.current) {
            clearInterval(timerIntervalRef.current);
            timerIntervalRef.current = null;
        }
    }, []);

    // Checkpoint: save progress to server every 5 minutes
    const sendCheckpoint = useCallback(async () => {
        const session = sessionRef.current;
        if (!session) return;
        try {
            await authFetch(`${API_URL}/api/v1/study_sessions/${session.session_id}/checkpoint`, {
                method: 'PUT',
                body: JSON.stringify({ total_seconds: secondsRef.current })
            });
        } catch (error) {
            console.error('Checkpoint error:', error);
        }
    }, []);

    const startCheckpointInterval = useCallback(() => {
        if (checkpointIntervalRef.current) return;
        checkpointIntervalRef.current = setInterval(() => {
            sendCheckpoint();
        }, 5 * 60 * 1000); // 5 minutes
    }, [sendCheckpoint]);

    const stopCheckpointInterval = useCallback(() => {
        if (checkpointIntervalRef.current) {
            clearInterval(checkpointIntervalRef.current);
            checkpointIntervalRef.current = null;
        }
    }, []);

    // Restore active session on mount (이전 세션 이어서 진행)
    useEffect(() => {
        if (!user) return;

        const checkActiveSession = async () => {
            try {
                const response = await authFetch(`${API_URL}/api/v1/study_sessions/active`);
                const data = await response.json();
                if (data.success && data.session) {
                    const session = data.session;
                    const subject = session.study_subject || {};
                    // 활성 세션 복원 — 사용자가 직접 종료를 결정할 수 있도록
                    const schedule = session.time_table || {};
                    setActiveSession({
                        ...session,
                        subject_name: subject.subjectname || '',
                        special_text: schedule.special_text || null,
                        subject_color: subject.color || '#ccc',
                        schedule_type: schedule.schedule_type || null,
                    });
                    secondsRef.current = session.total_seconds || 0;
                    setDisplaySeconds(session.total_seconds || 0);
                    // active 상태면 타이머 재시작
                    if (session.status === 'active') {
                        startTimerInterval();
                        startCheckpointInterval();
                    }
                    toast.info(t('timer.crashRecovered'));
                }
            } catch (error) {
                console.error('Error checking active session:', error);
            }
        };

        checkActiveSession();
        fetchTodaySessions();
        fetchTodaySummary();
    }, [user, t, fetchTodaySessions, fetchTodaySummary, startTimerInterval, startCheckpointInterval]);

    // beforeunload & visibilitychange handlers
    useEffect(() => {
        const handleBeforeUnload = () => {
            const session = sessionRef.current;
            if (session && (session.status === 'active' || session.status === 'paused')) {
                const token = localStorage.getItem('accessToken');
                // fetch keepalive: sendBeacon과 달리 Authorization 헤더 포함 가능
                fetch(`${API_URL}/api/v1/study_sessions/${session.session_id}/checkpoint`, {
                    method: 'PUT',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({ total_seconds: secondsRef.current }),
                    keepalive: true
                }).catch(() => {}); // 페이지 종료 중이므로 에러 무시
            }
        };

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'hidden') {
                sendCheckpoint();
            } else if (document.visibilityState === 'visible') {
                // Re-sync time when tab becomes visible again
                // The timer interval using Date.now() handles this automatically
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [sendCheckpoint]);

    const startSession = async (subjectId, scheduleId, subjectInfo) => {
        try {
            let response = await authFetch(`${API_URL}/api/v1/study_sessions`, {
                method: 'POST',
                body: JSON.stringify({ subject_id: subjectId, schedule_id: scheduleId })
            });
            let data = await response.json();

            // If failed due to existing active session, auto-recover and retry
            if (!data.success && response.status === 400) {
                const activeRes = await authFetch(`${API_URL}/api/v1/study_sessions/active`);
                const activeData = await activeRes.json();
                if (activeData.success && activeData.session) {
                    await authFetch(`${API_URL}/api/v1/study_sessions/${activeData.session.session_id}/recover`, {
                        method: 'PUT',
                        body: JSON.stringify({ total_seconds: activeData.session.total_seconds })
                    });
                    toast.info(t('timer.crashRecovered'));

                    // Retry start
                    response = await authFetch(`${API_URL}/api/v1/study_sessions`, {
                        method: 'POST',
                        body: JSON.stringify({ subject_id: subjectId, schedule_id: scheduleId })
                    });
                    data = await response.json();
                }
            }

            if (!data.success) {
                throw new Error(data.message);
            }

            const session = {
                ...data.session,
                subject_name: subjectInfo.subject_name,
                special_text: subjectInfo.special_text || null,
                subject_color: subjectInfo.subject_color,
                schedule_start: subjectInfo.start_time,
                schedule_duration: subjectInfo.scheduled_time,
                status: 'active'
            };

            setActiveSession(session);
            secondsRef.current = 0;
            setDisplaySeconds(0);
            startTimerInterval();
            startCheckpointInterval();

            return session;
        } catch (error) {
            console.error('Error starting session:', error);
            throw error;
        }
    };

    const pauseSession = async () => {
        if (!activeSession) return;
        // 타이머를 먼저 멈추되, 실패 시 복구할 수 있도록 상태 저장
        const wasRunning = !!timerIntervalRef.current;
        stopTimerInterval();
        try {
            const response = await authFetch(`${API_URL}/api/v1/study_sessions/${activeSession.session_id}/pause`, {
                method: 'PUT',
                body: JSON.stringify({ total_seconds: secondsRef.current })
            });
            const data = await response.json();
            if (data.success) {
                setActiveSession(prev => ({ ...prev, status: 'paused', pause_count: (prev.pause_count || 0) + 1 }));
            } else {
                // API가 success:false 반환 시 타이머 복구
                if (wasRunning) startTimerInterval();
            }
        } catch (error) {
            console.error('Error pausing session:', error);
            // API 실패 시 타이머 복구
            if (wasRunning) startTimerInterval();
        }
    };

    const resumeSession = async () => {
        if (!activeSession) return;
        try {
            const response = await authFetch(`${API_URL}/api/v1/study_sessions/${activeSession.session_id}/resume`, {
                method: 'PUT'
            });
            const data = await response.json();
            if (data.success) {
                setActiveSession(prev => ({ ...prev, status: 'active' }));
                startTimerInterval();
            }
        } catch (error) {
            console.error('Error resuming session:', error);
        }
    };

    const stopSession = async () => {
        if (!activeSession) return;
        const savedSeconds = secondsRef.current;
        const wasRunning = !!timerIntervalRef.current;
        stopTimerInterval();
        stopCheckpointInterval();
        try {
            const response = await authFetch(`${API_URL}/api/v1/study_sessions/${activeSession.session_id}/stop`, {
                method: 'PUT',
                body: JSON.stringify({ total_seconds: savedSeconds })
            });
            const data = await response.json();
            if (data.success) {
                setActiveSession(null);
                secondsRef.current = 0;
                setDisplaySeconds(0);
                fetchTodaySessions();
                fetchTodaySummary();
            } else {
                // API가 success:false → 타이머/체크포인트 복구
                if (wasRunning) startTimerInterval();
                startCheckpointInterval();
            }
        } catch (error) {
            console.error('Error stopping session:', error);
            // 네트워크 에러 시 타이머/체크포인트 복구
            if (wasRunning) startTimerInterval();
            startCheckpointInterval();
        }
    };

    // Cleanup intervals on unmount
    useEffect(() => {
        return () => {
            stopTimerInterval();
            stopCheckpointInterval();
        };
    }, [stopTimerInterval, stopCheckpointInterval]);

    const value = {
        activeSession,
        todaySessions,
        todaySummary,
        displaySeconds,
        startSession,
        pauseSession,
        resumeSession,
        stopSession,
        fetchTodaySessions,
        fetchTodaySummary
    };

    return (
        <TimerContext.Provider value={value}>
            {children}
        </TimerContext.Provider>
    );
};

export const useTimer = () => {
    const context = useContext(TimerContext);
    if (!context) {
        throw new Error('useTimer must be used within a TimerProvider');
    }
    return context;
};
